/**
 * Messages that carry a value inside them.
 *
 * A message like "Line 2 has no account code" cannot be translated by looking
 * the whole sentence up, because the number changes every time. Nor can it be
 * built by joining translated fragments — "Reference" + value + "already exists"
 * — because Arabic does not order words the way English does, so the result
 * reads wrong even when every individual word is right.
 *
 * So a message is a code plus its values. Each language holds one template per
 * code and decides for itself where the values sit in the sentence.
 */

export type MessageCode =
  | 'checks_passed'
  | 'checks_failed'
  | 'duplicate_reference'
  | 'line_missing_account'
  | 'lines_missing_description'
  | 'lines_missing_account'
  | 'lines_invalid_tax'
  | 'line_count'
  | 'tax_rates_cached'
  | 'no_reference_entered'
  | 'no_match_for_reference'
  | 'arabic_digits_folded'
  | 'digits_normalised'
  | 'contact_resolved'
  | 'no_contact_selected'
  | 'from_chart_of_accounts'
  | 'document_created'
  | 'rate_limited';

export interface Message {
  code: MessageCode;
  values?: Record<string, string | number>;
}

export const message = (code: MessageCode, values?: Record<string, string | number>): Message => ({
  code,
  ...(values ? { values } : {}),
});

/**
 * English templates. Arabic lives with the client, alongside the rest of the
 * translations, and uses the same placeholders in whatever order Arabic needs.
 */
export const EN_TEMPLATES: Record<MessageCode, string> = {
  checks_passed: 'All checks passed',
  checks_failed: 'Failed pre-push checks',
  duplicate_reference: 'Reference {reference} already exists in Xero',
  line_missing_account: 'Line {line} has no account code',
  lines_missing_description: '{count} line(s) missing a description',
  lines_missing_account: '{count} line(s) missing an account code',
  lines_invalid_tax: '{count} line(s) with a tax rate this organisation does not have',
  line_count: '{count} lines',
  tax_rates_cached: '{count} tax rates, from the cached list',
  no_reference_entered: 'No reference entered — skipped',
  no_match_for_reference: 'No match for {reference}',
  arabic_digits_folded: 'Arabic-Indic digits found and converted before mapping',
  digits_normalised: 'Typed input is normalised; the interface shows Western digits throughout',
  contact_resolved: 'Resolved to {name}',
  no_contact_selected: 'No contact selected',
  from_chart_of_accounts: 'From the chart of accounts',
  document_created: 'Created in Xero as DRAFT',
  rate_limited: 'Xero is rate limiting; retrying in {seconds}s',
};

/**
 * Fill the gaps in a template.
 *
 * A placeholder with no matching value is left as it is rather than becoming
 * "undefined" — a visibly wrong sentence is easier to notice and fix than a
 * plausible one with a hole in it.
 */
export function formatMessage(template: string, values: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole,
  );
}

/** Render a message in English. The client does the same with Arabic templates. */
export function renderMessage(msg: Message): string {
  return formatMessage(EN_TEMPLATES[msg.code], msg.values);
}
